import type { PostHog } from 'posthog-js';

const POSTHOG_ENABLED = ['1', 'true', 'yes', 'on'].includes(
  String(import.meta.env.VITE_PUBLIC_POSTHOG_ENABLED ?? '').toLowerCase(),
);
const POSTHOG_PROJECT_TOKEN = String(import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ?? '');
const POSTHOG_HOST = String(import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? '');
const APP_NAME = 'check-insta-channel-fit';
const EVENT_PREFIX = 'check-insta-channel-fit';
const LOCAL_STORAGE_KEY = 'ph_check-insta-channel-fit_anonymous_id';

let posthogClient: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;

function generateAnonymousId(): { id: string | null; isNew: boolean } {
  if (typeof window === 'undefined') {
    return { id: null, isNew: false };
  }

  const existingId = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (existingId) {
    return { id: existingId, isNew: false };
  }

  const newId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `anon_${Math.random().toString(36).slice(2)}${Date.now()}`;

  window.localStorage.setItem(LOCAL_STORAGE_KEY, newId);
  return { id: newId, isNew: true };
}

/**
 * Lädt posthog-js nur per dynamischem Import nach, wenn die Env-Vars tatsächlich
 * gesetzt sind -- damit bleibt das Hauptbundle für alle ohne PostHog-Konfiguration
 * unverändert klein (posthog-js landet dann in einem separaten, ungeladenen Chunk).
 */
export function initPosthog(): Promise<PostHog | null> {
  if (initPromise) return initPromise;

  if (
    typeof window === 'undefined' ||
    !POSTHOG_ENABLED ||
    !POSTHOG_PROJECT_TOKEN ||
    !POSTHOG_HOST
  ) {
    initPromise = Promise.resolve(null);
    return initPromise;
  }

  initPromise = import('posthog-js').then(({ default: posthog }) => {
    const { id: anonymousId, isNew } = generateAnonymousId();

    posthog.init(POSTHOG_PROJECT_TOKEN, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,
      persistence: 'localStorage',
    });

    if (anonymousId) {
      posthog.identify(anonymousId);
    }

    posthog.register({ app_name: APP_NAME });
    posthogClient = posthog;

    if (isNew && anonymousId) {
      trackEvent('visitor_created', { anonymous_id: anonymousId });
    }

    return posthogClient;
  });

  return initPromise;
}

export function getAnonymousId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(LOCAL_STORAGE_KEY);
}

function prefixedEventName(event: string): string {
  return event.startsWith(`${EVENT_PREFIX}_`) ? event : `${EVENT_PREFIX}_${event}`;
}

function baseProps(properties?: Record<string, unknown>): Record<string, unknown> {
  const anonymousId = getAnonymousId();
  return {
    ...properties,
    app_name: APP_NAME,
    anonymous_id: anonymousId ?? undefined,
    data_type: 'anonymous',
  };
}

/**
 * No-Op, solange initPosthog() noch nicht aufgelöst ist oder PostHog deaktiviert
 * ist -- kein Fehler, kein Crash. Frühe Events können dadurch vereinzelt verloren
 * gehen, während der dynamische Import noch lädt; das ist ein bewusster Kompromiss
 * zugunsten eines kleinen Hauptbundles.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!posthogClient) return;
  posthogClient.capture(prefixedEventName(event), baseProps(properties));
}
